import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

import type { User } from '@/api/auth';
import { snakify } from '@/helpers/common';
import { useGetCommonStore } from '@/store';

const INTERCOM_APP_ID = 'ozlaecfj';

const COMPANY = {
  companyId: '0',
  name: 'LU',
};

interface Intercom {
  init: () => void;
  identifyUser: (user: User, countryCode: string | undefined) => void;
  openChat: () => void;
  onHide: (callback: () => void) => void;
  offHide: (callback: () => void) => void;
}

const onHideHandlers: Set<() => void> = new Set();
let isOnHideGloballySubscribed = false;

export const Intercom: Intercom = {
  init: () => {
    try {
      // @ts-ignore
      window.Intercom(
        'boot',
        snakify({
          appId: INTERCOM_APP_ID,
          hideDefaultLauncher: true,
          customLauncherSelector: '#intercomcustomLauncher',
          company: COMPANY,
          casino: COMPANY.name,
        })
      );

      if (!isOnHideGloballySubscribed) {
        // @ts-ignore
        window.Intercom?.('onHide', () => {
          onHideHandlers.forEach((handler) => {
            try {
              handler();
            } catch (err) {
              console.error('Error in Intercom onHide handler', err);
            }
          });
        });

        isOnHideGloballySubscribed = true;
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        message: 'Intercom init failed',
        category: 'intercom',
        level: 'error',
      });

      console.error('Intercom init failed', error);
    }
  },
  identifyUser: (user, countryCode) => {
    try {
      // @ts-ignore
      window.Intercom(
        'update',
        snakify({
          email: user.email,
          userId: `${COMPANY.name}-${user.id}`,
          company: COMPANY,
          createdAt: user.createdAt,
          name:
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : '',
          phone: user.mobilePhone,
          verifiedEmail: user.confirmedAt ? 'Yes' : 'No',
          countryByIp: countryCode || null,
          profileCountry: user.country,
          groups: user.statuses?.map(({ id }) => id).join(', '),
          dateOfBirth: user.dateOfBirth,
          gender: user.gender,
        })
      );
    } catch (error) {
      Sentry.addBreadcrumb({
        message: 'Intercom identifyUser failed',
        category: 'intercom',
        level: 'error',
      });

      console.error('Intercom identifyUser failed', error);
    }
  },
  openChat: () => {
    try {
      // @ts-ignore
      window.Intercom('show');
    } catch (error) {
      Sentry.addBreadcrumb({
        message: 'Intercom openChat failed',
        category: 'intercom',
        level: 'error',
      });

      console.error('Intercom openChat failed', error);
    }
  },

  // Register a callback to be called when the Intercom chat is hidden (e.g., user closes the messenger)
  onHide: (callback) => {
    onHideHandlers.add(callback);
  },

  // Unregister a previously registered onHide callback — make sure to clean up in useEffect return
  offHide: (callback) => {
    onHideHandlers.delete(callback);
  },
};

export const useIntercom = () => {
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const countryCode = useCommonStore.use.countryCode();

  useEffect(() => {
    Intercom.init();
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    Intercom.identifyUser(user, countryCode);
  }, [user, countryCode]);

  return;
};
